import { differenceInSeconds } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { Button } from '~/components/Button';
import { showToast } from '~/components/Toast';
import { Toggle } from '~/components/Toggle';
import { useAnalytics } from '~/modules/monitoring/analytics/useAnalytics';
import { CTABlock } from '~/modules/settings/components/CTABlock';
import { SettingsPageHeader } from '~/modules/settings/components/SettingsPageHeader';
import { SETTINGS_ROUTES } from '~/modules/settings/config/routes';
import type { LabelFormSchema } from '~/modules/settings/modules/label/components/LabelForm';
import { LabelForm } from '~/modules/settings/modules/label/components/LabelForm';
import type { LabelDetail } from '~settings/api/lib/schemas';
import { useLabelCreate } from '~settings/api/mutations/labelCreate';
import {
  getErrorMessage,
  getLabelPayload,
} from '~settings/modules/label/helpers';

const FORM_ID = 'createLabelForm';

export function CreateLabelPage() {
  const [isActive, setIsActive] = useState(true);
  const { t } = useTranslation('settings');
  const navigate = useNavigate();

  const pageOpenedAt = useRef<number>();
  const { trackEvent } = useAnalytics('ADA: User management');

  const { mutate: createLabel, isPending } = useLabelCreate({
    onSuccess: (data: LabelDetail) => {
      const labelCreateSecondsPassed = pageOpenedAt.current
        ? differenceInSeconds(new Date(), pageOpenedAt.current, {
            roundingMethod: 'ceil',
          })
        : 0;

      const analyticsParams = {
        time_to_create_label_seconds: labelCreateSecondsPassed,
        label_name: data.name,
        label_pmo: data.pmo ?? '',
        parent_label_name: data.parentName ?? '',
        parent_label_id: data.parentId ?? '',
        is_sublabel: !!data.parentId,
        created_at: Date.now(),
      };

      trackEvent('labelCreateSuccess', analyticsParams);
      navigate(SETTINGS_ROUTES.LABEL_DETAIL(data.id));
    },
    onError: async (error) => {
      const message = await getErrorMessage(error, t, true);

      showToast(message);
    },
  });

  useEffect(() => {
    pageOpenedAt.current = Date.now();
  }, []);

  const onSubmit = (data: LabelFormSchema) => {
    const payload = getLabelPayload(data, isActive);

    createLabel(payload);
  };

  return (
    <article className="relative flex min-h-full flex-col">
      <SettingsPageHeader
        breadcrumbItems={[
          { to: SETTINGS_ROUTES.ROOT, label: 'title' },
          { to: SETTINGS_ROUTES.LABEL_LIST, label: 'labels' },
          { to: '', label: 'label.createLabelTitle' },
        ]}
        title="label.createLabelTitle"
        actions={
          <Toggle
            pressed={isActive}
            onPressedChange={setIsActive}
            activeLabel={t('active')}
            inactiveLabel={t('inactive')}
          />
        }
      />
      <div className="grow overflow-y-auto pb-xl">
        <LabelForm isActive={isActive} onSubmit={onSubmit} id={FORM_ID} />
      </div>
      <CTABlock>
        <Button asChild variant="secondary" size="large">
          <Link to={SETTINGS_ROUTES.LABEL_LIST}>{t('buttons.cancel')}</Link>
        </Button>
        <Button
          variant="primary"
          type="submit"
          form={FORM_ID}
          size="large"
          disabled={isPending}
        >
          {t('label.addLink')}
        </Button>
      </CTABlock>
    </article>
  );
}

CreateLabelPage.displayName = 'CreateLabelPage';
